import React, { useState, useEffect, useRef } from 'react';
import SpaLink from './SpaLink';
import { LIVE_PRODUCTS } from './products';

/**
 * ProductsMenu (Nov 1, 2026) — the "Products ▾" button in the marketing-page
 * headers (home, /groups, /events, /games).
 *
 * Opens on a click or tap, never on hover, so it works the same on a phone.
 * On a phone the panel spans the header's width just under it, with big rows.
 * The links are always in the page (hidden while closed) so crawlers still
 * see them. Closes on outside click, Escape, or choosing a product.
 *
 * The list comes from products.js — a product appears here when it goes live.
 */

const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

export const ProductIcon = ({ productKey, size = 22 }) => {
  const common = { width: size, height: size, viewBox: '0 0 24 24', 'aria-hidden': true, ...stroke };
  switch (productKey) {
    case 'groups':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
          <circle cx="17" cy="9" r="2.6" />
          <path d="M16 14.2c2.9.3 5 2.6 5 5.8" />
        </svg>
      );
    case 'leagues':
      return (
        <svg {...common}>
          <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
        </svg>
      );
    case 'events':
      return (
        <svg {...common}>
          <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z" />
          <path d="M17 6h3v2a3 3 0 0 1-3 3M7 6H4v2a3 3 0 0 0 3 3" />
        </svg>
      );
    case 'associations':
      return (
        <svg {...common}>
          <path d="M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6l8-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case 'clubs':
      return (
        <svg {...common}>
          <path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" />
        </svg>
      );
    default:
      return null;
  }
};

const ProductsMenu = ({ isMobile, location = 'header', current }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const panelStyle = isMobile
    ? {
        position: 'absolute', top: '100%', left: 8, right: 8, marginTop: 6,
        background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 16,
        boxShadow: '0 16px 40px rgba(17, 24, 39, 0.18)', padding: 8,
        display: open ? 'flex' : 'none', flexDirection: 'column', gap: 4, zIndex: 200,
        textAlign: 'left',
      }
    : {
        position: 'absolute', top: '100%', left: 0, marginTop: 8, width: 400,
        background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14,
        boxShadow: '0 16px 40px rgba(17, 24, 39, 0.14)', padding: 8,
        display: open ? 'flex' : 'none', flexDirection: 'column', gap: 2, zIndex: 200,
        textAlign: 'left',
      };

  return (
    // On a phone the wrapper is not positioned, so the panel anchors to the
    // (fixed or sticky) header and spans its width.
    <div ref={wrapRef} style={{ position: isMobile ? 'static' : 'relative' }}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="tto-products-menu"
        onClick={() => {
          if (!open) trackEvent('products_menu_open', { location });
          setOpen((o) => !o);
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isMobile ? '10px 12px' : '10px 16px', minHeight: 44,
          fontFamily: 'inherit', fontSize: isMobile ? 14 : 15, fontWeight: 700,
          color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Products
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" {...stroke} strokeWidth={2.5}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <div id="tto-products-menu" style={panelStyle}>
        {LIVE_PRODUCTS.map((p) => (
          <SpaLink
            key={p.key}
            to={p.to}
            aria-current={current === p.key ? 'page' : undefined}
            onClick={() => {
              trackEvent('products_menu_click', { product: p.key, location });
              setOpen(false);
            }}
            style={{
              display: 'flex', gap: 14, alignItems: isMobile ? 'center' : 'flex-start',
              padding: isMobile ? '10px 12px' : '12px 14px', minHeight: isMobile ? 64 : 44,
              borderRadius: 12, textDecoration: 'none', color: '#111827',
              background: current === p.key ? '#f0fdf4' : 'transparent',
              border: isMobile ? '1px solid #e5e7eb' : '1px solid transparent',
            }}
          >
            <span style={{
              flex: 'none', width: isMobile ? 44 : 40, height: isMobile ? 44 : 40, borderRadius: 10,
              background: '#dcfce7', color: '#166534',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ProductIcon productKey={p.key} size={isMobile ? 24 : 22} />
            </span>
            <span>
              <span style={{ display: 'block', fontSize: isMobile ? 17 : 16, fontWeight: 700 }}>{p.name}</span>
              <span style={{ display: 'block', fontSize: 14, color: '#4b5563', lineHeight: 1.4, marginTop: 2 }}>{p.line}</span>
            </span>
          </SpaLink>
        ))}
      </div>
    </div>
  );
};

export default ProductsMenu;
